import React from 'react';
import { injectIntl, WrappedComponentProps } from 'react-intl';
import '../../resources/css/order.css';
import { isURL } from '../../resources/utils';
import { RouterProps } from '../../types/generalTypes';
import {
    ORDER_CLAIM_INIT_ERRORS,
    PaymentError,
    PaymentErrorResponse,
} from '../../types/paymentErrors';
import Button from '../common/button';
import Container from '../common/container';
import LoadingCircle from '../common/loadingCircle';
import Snackbar from '../common/snackbar';
import OrderApi from './api/order_api';

const CLAIM_INIT_STORAGE_KEY = 'claimInitPayload';
const CLAIM_INIT_STORAGE_TTL_MS = 15 * 60 * 1000;

const storeClaimInitPayload = (payload: any) => {
    if (typeof sessionStorage === 'undefined') return;
    try {
        sessionStorage.setItem(
            CLAIM_INIT_STORAGE_KEY,
            JSON.stringify({ ...payload, createdAt: Date.now() }),
        );
    } catch (error) {
        // ignore storage errors (quota, disabled, etc.)
    }
};

const readClaimInitPayload = () => {
    if (typeof sessionStorage === 'undefined') return null;
    try {
        const raw = sessionStorage.getItem(CLAIM_INIT_STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed?.request || !parsed?.email) return null;
        if (parsed?.createdAt && Date.now() - parsed.createdAt > CLAIM_INIT_STORAGE_TTL_MS) {
            sessionStorage.removeItem(CLAIM_INIT_STORAGE_KEY);
            return null;
        }
        return parsed;
    } catch (error) {
        return null;
    }
};

interface IProps {}

type Props = IProps & WrappedComponentProps & RouterProps;

interface State {
    /** Erreur affichée en corps de page (bloquante) */
    error: boolean;
    /** Message principal d'erreur */
    errorMessage: string;
    /** Message secondaire (détail Saferpay, etc.) */
    errorDetail: string;
    /** Snackbar visible */
    snackbarOpen: boolean;
    /** Le bouton "réessayer" est proposé */
    canRetry: boolean;
    /** Le bouton "retour" est proposé */
    showBackButton: boolean;
}

class ClaimInit extends React.Component<Props, State> {
    private mounted = false;

    /** Arguments mémorisés pour un éventuel retry */
    private paymentArgs: any[] = [];

    constructor(props: Props) {
        super(props);

        const { intl } = this.props;

        this.state = {
            error: false,
            errorMessage: intl.formatMessage({ id: 'order.checkFailed' }),
            errorDetail: '',
            snackbarOpen: false,
            canRetry: false,
            showBackButton: false,
        };
    }

    componentDidMount() {
        this.mounted = true;
        this.bootstrapPayment();
    }

    componentWillUnmount() {
        this.mounted = false;
    }

    // ------------------------------------------------------------------
    // Chargement du payload (state / sessionStorage)
    // ------------------------------------------------------------------
    private handleMissingPayload = () => {
        const { intl } = this.props;
        this.setState({
            error: true,
            errorMessage: intl.formatMessage({ id: 'order.error.sessionExpired' }),
            errorDetail: '',
            snackbarOpen: true,
            canRetry: false,
            showBackButton: true,
        });
    };

    // ------------------------------------------------------------------
    // Gestion des erreurs de paiement structurées
    // ------------------------------------------------------------------

    handlePaymentError = (data: PaymentErrorResponse) => {
        const { intl } = this.props;

        switch (data.codeError) {
            case ORDER_CLAIM_INIT_ERRORS.SERIALIZER_ERROR:
                // p_ocin_100 - champs invalides
                this.setState({
                    error: true,
                    errorMessage: intl.formatMessage({ id: 'order.error.invalidFields' }),
                    snackbarOpen: true,
                    showBackButton: true,
                });
                break;

            case ORDER_CLAIM_INIT_ERRORS.INVALID_AMOUNT:
                // p_ocin_102 - montant nul ou invalide
                this.setState({
                    error: true,
                    errorMessage: intl.formatMessage({ id: 'order.error.invalidAmount' }),
                    snackbarOpen: true,
                    showBackButton: true,
                });
                break;

            case ORDER_CLAIM_INIT_ERRORS.SAFERPAY_ERROR:
                // p_ocin_103 - erreur métier Saferpay
                this.handleSaferpayError(data);
                break;

            case ORDER_CLAIM_INIT_ERRORS.UNEXPECTED_ERROR:
            default:
                // p_ocin_101 - erreur inattendue
                this.setState({
                    error: true,
                    errorMessage: intl.formatMessage({ id: 'order.error.unexpected' }),
                    snackbarOpen: true,
                    showBackButton: true,
                });
                break;
        }
    };

    handleSaferpayError = (data: PaymentErrorResponse) => {
        const { intl } = this.props;
        const behavior = data.saferpay?.behavior ?? 'DO_NOT_RETRY';
        const detail = data.saferpay?.errorMessage
            || data.saferpay?.errorDetail?.join(' ')
            || '';

        switch (behavior) {
            case 'RETRY':
                this.setState({
                    error: true,
                    errorMessage: intl.formatMessage({ id: 'order.error.saferpay.retry' }),
                    errorDetail: detail,
                    snackbarOpen: true,
                    canRetry: true,
                });
                break;

            case 'RETRY_LATER':
                this.setState({
                    error: true,
                    errorMessage: intl.formatMessage({ id: 'order.error.saferpay.retryLater' }),
                    errorDetail: detail,
                    snackbarOpen: true,
                    showBackButton: true,
                });
                break;

            case 'OTHER_MEANS':
                this.setState({
                    error: true,
                    errorMessage: intl.formatMessage({ id: 'order.error.saferpay.otherMeans' }),
                    errorDetail: detail,
                    snackbarOpen: true,
                    showBackButton: true,
                });
                break;

            case 'DO_NOT_RETRY':
            default:
                this.setState({
                    error: true,
                    errorMessage: intl.formatMessage({ id: 'order.error.saferpay.doNotRetry' }),
                    errorDetail: detail,
                    snackbarOpen: true,
                    showBackButton: true,
                });
                break;
        }
    };

    // ------------------------------------------------------------------
    // Appel API
    // ------------------------------------------------------------------

    async getPaymentUrl(
        email: any,
        client: any,
        toPay: any,
        code: any,
        schoolId: any,
        departmentId: any,
        noLogin: any,
    ) {
        const { match } = this.props;

        // Mémoriser les arguments pour un éventuel retry
        this.paymentArgs = [
            email,
            client,
            toPay,
            code,
            schoolId,
            departmentId,
            noLogin,
        ];

        try {
            const response = await OrderApi.initializeClaimPayment(
                email,
                client,
                toPay,
                code,
                schoolId,
                departmentId,
                noLogin,
                match.params.lang ? match.params.lang : 'fr',
            );

            if (!this.mounted) return;

            if (isURL(response)) {
                // Succès : redirection vers Saferpay
                window.location.href = response;
            } else {
                // Réponse 200 mais pas une URL → traiter comme erreur
                const { intl } = this.props;
                this.setState({
                    error: true,
                    errorMessage: intl.formatMessage({ id: 'order.error.unexpected' }),
                    snackbarOpen: true,
                    showBackButton: true,
                });
            }
        } catch (error) {
            if (!this.mounted) return;

            // Erreur structurée provenant du backend
            if (error instanceof PaymentError) {
                this.handlePaymentError(error.data);
                return;
            }

            // Erreur réseau ou non prévue → message générique
            const { intl } = this.props;
            this.setState({
                error: true,
                errorMessage: intl.formatMessage({ id: 'order.error.unexpected' }),
                snackbarOpen: true,
                showBackButton: true,
            });
        }
    }

    private bootstrapPayment = () => {
        const { history } = this.props;
        const payloadFromState = history.location.state && history.location.state.request
            ? history.location.state
            : null;

        const payload = payloadFromState || readClaimInitPayload();

        if (!payload || !payload.request || !payload.email) {
            this.handleMissingPayload();
            return;
        }

        if (payloadFromState) {
            storeClaimInitPayload(payloadFromState);
        }

        this.initPaymentFromPayload(payload);
    };

    private initPaymentFromPayload = (payload: any) => {
        const { request, email } = payload;
        const noLogin = payload.noLogin ?? false;
        const clientId = payload.clientId ?? 'undefined';

        if (!request) {
            this.handleMissingPayload();
            return;
        }

        localStorage?.setItem('clientId', clientId || 'undefined');

        this.getPaymentUrl(
            email,
            request.Client,
            request.toPay,
            request.code,
            request.schoolId,
            request.departmentId,
            noLogin,
        );
    };

    // ------------------------------------------------------------------
    // Actions utilisateur
    // ------------------------------------------------------------------

    handleRetry = () => {
        if (this.paymentArgs.length === 0) return;
        // Reset de l'état d'erreur
        this.setState({
            error: false,
            errorMessage: '',
            errorDetail: '',
            snackbarOpen: false,
            canRetry: false,
            showBackButton: false,
        });
        const [
            email,
            client,
            toPay,
            code,
            schoolId,
            departmentId,
            noLogin,
        ] = this.paymentArgs;
        this.getPaymentUrl(
            email,
            client,
            toPay,
            code,
            schoolId,
            departmentId,
            noLogin,
        );
    };

    handleGoBack = () => {
        const { history } = this.props;
        history.goBack();
    };

    // ------------------------------------------------------------------
    // Rendu
    // ------------------------------------------------------------------

    render() {
        const { intl } = this.props;
        const {
            error,
            errorMessage,
            errorDetail,
            snackbarOpen,
            canRetry,
            showBackButton,
        } = this.state;

        return (
            <Container headerBase={intl.formatMessage({ id: 'order.order' })}>
                <div className="__order-parent">
                    {error ? (
                        <>
                            <p className="__order-text">{errorMessage}</p>
                            {errorDetail && (
                                <p className="__order-text" style={{ fontSize: '0.9em', opacity: 0.8 }}>
                                    {errorDetail}
                                </p>
                            )}
                            <div
                                style={{
                                    display: 'flex',
                                    gap: '12px',
                                    marginTop: '16px',
                                    justifyContent: 'center',
                                }}
                            >
                                {canRetry && (
                                    <Button
                                        buttonClasses="__order-button"
                                        onClick={this.handleRetry}
                                    >
                                        {intl.formatMessage({ id: 'order.error.retryButton' })}
                                    </Button>
                                )}
                                {showBackButton && (
                                    <Button
                                        buttonClasses="__order-button"
                                        onClick={this.handleGoBack}
                                    >
                                        {intl.formatMessage({ id: 'order.error.goBack' })}
                                    </Button>
                                )}
                            </div>
                        </>
                    ) : (
                        <>
                            <p className="__order-text">{intl.formatMessage({ id: 'order.init' })}</p>
                            <LoadingCircle />
                        </>
                    )}
                </div>
                <Snackbar
                    variant="error"
                    open={snackbarOpen}
                    onClose={() => this.setState({ snackbarOpen: false })}
                    timer={8000}
                    message={errorMessage}
                />
            </Container>
        );
    }
}

export default injectIntl(ClaimInit);
